import React from 'react';
import { createPortal } from 'react-dom';
import './BenefitsModal.css';

interface PlanOption {
  productId: number;
  productName: string;
  productLabel: string;
  maxBenefitLimit: number;
  planType: string;
  rate: {
    rateAmount: number;
    currency: {
      currencyId: number;
      currencyCode: string;
      symbol: string;
    };
  };
  benefitsCategories: Array<{
    benefitsCategory: string;
    categoryCoverage: string;
    benefits: Array<{
      benefitName: string;
      benefitCoverage: string;
    }>;
  }>;
  exclusion: string[];
  productOverview: Array<{
    productOverviewId: number;
    productId: number;
    description: string;
  }>;
}

interface BenefitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  planOptions: PlanOption[];
}

const BenefitsModal: React.FC<BenefitsModalProps> = ({
  isOpen,
  onClose,
  planId,
  planOptions
}) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Find the selected plan
  const selectedPlan = planOptions.find(plan => plan.productId.toString() === planId);

  if (!selectedPlan) {
    const errorModalContent = (
      <div className="benefits-modal-backdrop" onClick={handleBackdropClick}>
        <div className="benefits-modal-container">
          <div className="benefits-modal-header">
            <div className="benefits-modal-title-section">
              <h2 className="benefits-modal-title">Plan Not Found</h2>
            </div>
            <button
              className="benefits-modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
          <div className="benefits-modal-content">
            <p>The selected plan could not be found.</p>
          </div>
        </div>
      </div>
    );
    
    return createPortal(errorModalContent, document.body);
  }

  // Helper function to detect percentage values
  const isPercentage = (value: string): boolean => {
    return typeof value === 'string' && value.trim().endsWith('%');
  };

  // Format currency display
  const formatCurrency = (amount: string | number, currencyCode: string) => {
    if (typeof amount === 'string') {
      // If it's already formatted (like "2,500,000"), just add symbol
      return `${currencyCode} ${amount}`;
    }
    return `${currencyCode} ${amount.toLocaleString()}`;
  };

  // Process benefits to match the structure shown in the image
  const processedBenefits: Array<{
    benefitName: string;
    benefitCoverage: string;
    isSubBenefit?: boolean;
    parentCategory?: string;
    isCategory?: boolean;
  }> = [];

  selectedPlan.benefitsCategories.forEach(category => {
    // First, add the category itself with its coverage
    processedBenefits.push({
      benefitName: category.benefitsCategory,
      benefitCoverage: category.categoryCoverage === 'Included' || category.categoryCoverage === null
        ? 'Included'
        : category.categoryCoverage.includes(selectedPlan.rate.currency.currencyCode)
        ? category.categoryCoverage
        : isPercentage(category.categoryCoverage)
        ? category.categoryCoverage
        : formatCurrency(category.categoryCoverage, selectedPlan.rate.currency.currencyCode),
      isCategory: true
    });

    // Then add individual benefits within that category
    if (category.benefits && category.benefits.length > 0) {
      // Special handling for Emergency Assistance Benefits
      if (category.benefitsCategory === 'Emergency Assistance Benefits') {
        // Create bullet points from sub-benefits with line breaks
        const subBenefitsList = category.benefits
          .map(benefit => `• ${benefit.benefitName}`)
          .join('\n');
        
        processedBenefits.push({
          benefitName: 'Emergency Services',
          benefitCoverage: subBenefitsList || 'Included',
          isCategory: false
        });
      } else {
        // For other categories with individual benefits, show them normally
        category.benefits.forEach(benefit => {
          processedBenefits.push({
            benefitName: benefit.benefitName,
            benefitCoverage: benefit.benefitCoverage === 'Up to MBL'
              ? 'Up to MBL'
              : benefit.benefitCoverage === 'Included' || benefit.benefitCoverage === null || benefit.benefitCoverage === ''
              ? 'Included'
              : benefit.benefitCoverage && benefit.benefitCoverage.includes && benefit.benefitCoverage.includes(selectedPlan.rate.currency.currencyCode)
              ? benefit.benefitCoverage
              : benefit.benefitCoverage && isPercentage(benefit.benefitCoverage)
              ? benefit.benefitCoverage
              : benefit.benefitCoverage
              ? formatCurrency(benefit.benefitCoverage, selectedPlan.rate.currency.currencyCode)
              : 'Included',
            isCategory: false
          });
        });
      }
    }
  });

  const modalContent = (
    <div className="benefits-modal-backdrop" onClick={handleBackdropClick}>
      <div className="benefits-modal-container">
        {/* Modal Header */}
        <div className="benefits-modal-header">
          <div className="benefits-modal-title-section">
            <h2 className="benefits-modal-title">{selectedPlan.productLabel || selectedPlan.productName} Travel Insurance Benefits</h2>
          </div>
          <button
            className="benefits-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div className="benefits-modal-content">
          <div className="benefits-table-container">
            <table className="benefits-table">
              <thead>
                <tr>
                  <th className="benefits-table-header benefit-column">Benefit</th>
                  <th className="benefits-table-header plan-column">{selectedPlan.productLabel || selectedPlan.productName}</th>
                </tr>
              </thead>
              <tbody>
                {processedBenefits.map((benefit, index) => (
                  <tr key={index} className="benefits-table-row">
                    <td className={`benefits-table-cell benefit-name ${benefit.isCategory ? 'benefit-category' : ''}`}>
                      {benefit.benefitName}
                    </td>
                    <td className="benefits-table-cell benefit-value" style={{ whiteSpace: 'pre-line' }}>
                      {benefit.benefitCoverage}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal using React portal to ensure it's rendered at document body level
  return createPortal(modalContent, document.body);
};

export default BenefitsModal;